import { ComponentPropsWithoutRef, ReactNode, useEffect, useRef, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import {
    buildChartData,
    classifyColumns,
    extractTableData,
    inferChartType,
    isChartable,
} from './chatTableUtils';
import { AMP_CHART_FONT_COLOR, AMP_CHART_FONT_FAMILY, AMP_CHART_FONT_SIZE } from 'Core-utils/constants/charts';

Chart.register(...registerables);

const TableIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
        <line x1="3" y1="9" x2="21" y2="9" stroke="currentColor" strokeWidth="2" />
        <line x1="9" y1="9" x2="9" y2="21" stroke="currentColor" strokeWidth="2" />
    </svg>
);

const ChartIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="2" y="12" width="4" height="10" fill="currentColor" rx="1" />
        <rect x="10" y="6" width="4" height="16" fill="currentColor" rx="1" />
        <rect x="18" y="2" width="4" height="20" fill="currentColor" rx="1" />
    </svg>
);

const CHART_OPTIONS_BASE = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 300 },
    plugins: {
        legend: {
            display: true,
            position: 'bottom' as const,
            labels: {
                color: AMP_CHART_FONT_COLOR,
                font: { family: AMP_CHART_FONT_FAMILY, size: AMP_CHART_FONT_SIZE },
                usePointStyle: true,
                pointStyle: 'circle',
                padding: 12,
            },
        },
        tooltip: {
            mode: 'index' as const,
            intersect: false,
            backgroundColor: 'rgba(20, 20, 30, 0.95)',
            titleColor: AMP_CHART_FONT_COLOR,
            bodyColor: AMP_CHART_FONT_COLOR,
            borderColor: 'rgba(255,255,255,0.15)',
            borderWidth: 1,
            padding: 8,
            titleFont: { family: AMP_CHART_FONT_FAMILY, size: AMP_CHART_FONT_SIZE },
            bodyFont: { family: AMP_CHART_FONT_FAMILY, size: AMP_CHART_FONT_SIZE },
        },
    },
    scales: {
        x: {
            ticks: {
                color: AMP_CHART_FONT_COLOR,
                font: { family: AMP_CHART_FONT_FAMILY, size: 11 },
                maxRotation: 45,
            },
            grid: { color: 'rgba(255,255,255,0.08)' },
            border: { color: 'rgba(255,255,255,0.2)' },
        },
        y: {
            ticks: {
                color: AMP_CHART_FONT_COLOR,
                font: { family: AMP_CHART_FONT_FAMILY, size: 11 },
            },
            grid: { color: 'rgba(255,255,255,0.08)' },
            border: { color: 'rgba(255,255,255,0.2)' },
        },
    },
};

interface ChatTableWithChartProps extends ComponentPropsWithoutRef<'table'> {
    children?: ReactNode;
}

const ChatTableWithChart = ({ children, ...tableProps }: ChatTableWithChartProps) => {
    const [showChart, setShowChart] = useState(false);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const chartRef = useRef<Chart | null>(null);

    const tableData = extractTableData(children);
    const { headers, rows } = tableData;
    const chartable = isChartable(headers, rows);

    const classification = chartable ? classifyColumns(headers, rows) : null;
    const chartType = classification ? inferChartType(rows, classification.labelColumnIndex) : 'bar';
    const chartData = classification ? buildChartData(headers, rows, classification, chartType) : null;

    useEffect(() => {
        if (!showChart || !chartData || !canvasRef.current) {
            return;
        }

        const ctx = canvasRef.current.getContext('2d');
        if (!ctx) {
            return;
        }

        if (chartRef.current) {
            chartRef.current.destroy();
            chartRef.current = null;
        }

        chartRef.current = new Chart(ctx, {
            type: chartType,
            data: {
                labels: chartData.labels,
                datasets: chartData.datasets,
            },
            options: CHART_OPTIONS_BASE,
        });

        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
        };
    }, [showChart, chartData, chartType]);

    return (
        <div className="chat-message__table-wrap">
            {chartable && (
                <div className="chat-message__table-toggle">
                    <button
                        type="button"
                        className={`chat-message__table-toggle-btn${!showChart ? ' chat-message__table-toggle-btn--active' : ''}`}
                        onClick={() => setShowChart(false)}
                        aria-label="Show table"
                        title="Table view"
                    >
                        <TableIcon />
                    </button>
                    <button
                        type="button"
                        className={`chat-message__table-toggle-btn${showChart ? ' chat-message__table-toggle-btn--active' : ''}`}
                        onClick={() => setShowChart(true)}
                        aria-label="Show chart"
                        title="Chart view"
                    >
                        <ChartIcon />
                    </button>
                </div>
            )}

            {showChart ? (
                <div className="chat-message__chart-container">
                    <canvas ref={canvasRef} />
                </div>
            ) : (
                <table {...tableProps}>{children}</table>
            )}
        </div>
    );
};

export default ChatTableWithChart;
